/**
 * @fileoverview useNotifications Hook
 * @description Custom hook for managing notifications with polling.
 *              Provides unread count, notification list, and mark-as-read actions.
 * @module hooks/useNotifications
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { calendarApi } from '@/services/api/calendar';
import { apiClient } from '@/services/api/client';
import { incidentApi } from '@/features/IncidentAnalysis/services/incidentApi';
import { useAuth } from '@/context';
import { reportNotificationStore, REPORT_NOTIFY_EVENT } from '@/store/reportNotifications';

const POLL_INTERVAL = 30000; // 30 seconds

// LOC notifications API helper
const LOC_BASE = '/v1/lines-of-code';
const fetchLocNotifications = async () => {
  try {
    const resp = await apiClient.get(`${LOC_BASE}/notifications`);
    return resp?.data?.notifications || [];
  } catch {
    return [];
  }
};

const markLocNotificationRead = async (id) => {
  try {
    await apiClient.put(`${LOC_BASE}/notifications/${id}/read`);
  } catch {
    // silent
  }
};

/**
 * Custom hook for notifications
 * @param {Object} options
 * @param {boolean} options.enablePolling - Whether to poll for new notifications
 * @returns {Object} Notification state and actions
 */
export const useNotifications = ({ enablePolling = true } = {}) => {
  const { getUserProfile, isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);
  const reportStatusRef = useRef({ runId: 0, phase: 'idle' });
  const lastNotifiedRunRef = useRef(0);

  const user = getUserProfile();
  const userId = user?.email || user?.id || '';

  useEffect(() => {
    try {
      lastNotifiedRunRef.current = Number(sessionStorage.getItem('report_last_notified_run_id') || 0);
    } catch {
      lastNotifiedRunRef.current = 0;
    }
  }, []);

  const checkReportCompletion = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const status = await incidentApi.reportStatus();
      const runId = Number(status?.run_id || 0);
      const phase = status?.phase || 'idle';
      const computedDone = !status?.running
        && Number(status?.total || 0) > 0
        && Number(status?.completed || 0) >= Number(status?.total || 0);
      const isDone = phase === 'done' || computedDone;

      const prev = reportStatusRef.current;
      const transitionedToDone = isDone && (
        prev.runId !== runId
        || (prev.phase !== 'done' && prev.phase !== 'idle')
      );

      if (runId > 0 && transitionedToDone && runId > lastNotifiedRunRef.current) {
        const resultData = await incidentApi.reportResults({ run_id: runId }).catch(() => ({ results: [] }));
        reportNotificationStore.add({
          label: 'Incident Validation Report',
          csvRows: resultData?.results || [],
          runId,
        });
        lastNotifiedRunRef.current = runId;
        try {
          sessionStorage.setItem('report_last_notified_run_id', String(runId));
        } catch {
          // ignore storage errors
        }
      }

      reportStatusRef.current = { runId, phase: isDone ? 'done' : phase };
    } catch {
      // Silent by design: report polling is best-effort for bell notifications.
    }
  }, [isAuthenticated]);

  /**
   * Fetch unread notification count (calendar + LOC)
   */
  const fetchUnreadCount = useCallback(async () => {
    if (!userId) return;
    try {
      const [calResponse, locItems] = await Promise.all([
        calendarApi.getUnreadCount(userId).catch(() => ({ data: { unread_count: 0 } })),
        fetchLocNotifications(),
      ]);
      const calData = calResponse?.data ?? calResponse;
      const calCount = calData?.unread_count ?? 0;
      const locUnread = locItems.filter((n) => !n.is_read).length;
      const reportUnread = reportNotificationStore.getUnread().length;
      setUnreadCount(calCount + locUnread + reportUnread);
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
    }
  }, [userId]);

  /**
   * Fetch notification list (calendar + LOC merged)
   */
  const fetchNotifications = useCallback(async (options = {}) => {
    if (!userId) return [];
    setLoading(true);
    setError(null);
    try {
      const [calResult, locResult] = await Promise.allSettled([
        calendarApi.getNotifications(userId, {
          includeRead: options.includeRead ?? true,
          page: options.page || 1,
          pageSize: options.pageSize || 20,
        }),
        fetchLocNotifications(),
      ]);

      // Handle calendar result
      let calItems = [];
      if (calResult.status === 'fulfilled') {
        const payload = calResult.value?.data ?? calResult.value;
        calItems = Array.isArray(payload) ? payload
          : Array.isArray(payload?.items) ? payload.items
          : [];
      } else {
        setError(calResult.reason?.message || 'Failed to fetch notifications');
      }

      // Handle LOC result
      const resolvedLocItems = locResult.status === 'fulfilled' ? locResult.value : [];

      // Filter LOC notifications based on includeRead option
      const filteredLocItems = (options.includeRead ?? true)
        ? resolvedLocItems
        : resolvedLocItems.filter((n) => !n.is_read);

      // Normalize LOC notifications to match calendar format
      const normalizedLoc = filteredLocItems.map((n) => ({
        id: `loc_${n.id}`,
        _locId: n.id,
        _source: 'loc',
        title: 'Lines of Code Report',
        message: n.message,
        category: 'LOC_REPORT',
        is_read: n.is_read,
        created_at: n.created_at,
        triggered_by_name: null,
      }));

      // Normalize Report notifications
      const rawReportItems = (options.includeRead ?? true)
        ? reportNotificationStore.getAll()
        : reportNotificationStore.getUnread();
      const normalizedReports = rawReportItems.map((n) => ({
        id: n.id,
        _source: 'report',
        _runId: n.runId,
        _csvRows: n.csvRows,
        _label: n.label,
        title: 'Report Ready',
        message: `${n.label} is ready. Click to download.`,
        category: 'REPORT_READY',
        is_read: n.is_read,
        created_at: n.created_at,
        triggered_by_name: null,
      }));

      // Merge and sort by date
      const allItems = [...calItems, ...normalizedLoc, ...normalizedReports].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      setNotifications(allItems);
      return allItems;
    } catch (err) {
      setError(err.message);
      console.error('Failed to fetch notifications:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [userId]);

  /**
   * Mark a single notification as read
   */
  const markAsRead = useCallback(async (notificationId) => {
    if (!userId) return;
    try {
      // Check if this is a LOC notification
      const notification = notifications.find((n) => n.id === notificationId);
      if (notification?._source === 'loc') {
        await markLocNotificationRead(notification._locId);
      } else if (notification?._source === 'report') {
        reportNotificationStore.markRead(notificationId);
      } else {
        await calendarApi.markNotificationRead(notificationId, userId);
      }
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  }, [userId, notifications]);

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = useCallback(async () => {
    if (!userId) return;
    try {
      // Mark calendar notifications as read
      await calendarApi.markAllNotificationsRead(userId);
      // Mark all unread LOC notifications as read
      const unreadLoc = notifications.filter((n) => n._source === 'loc' && !n.is_read);
      await Promise.all(unreadLoc.map((n) => markLocNotificationRead(n._locId)));
      // Mark all report notifications as read
      reportNotificationStore.markAllRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  }, [userId, notifications]);

  /**
   * Clear past report notifications from bell history.
   * Note: calendar/LOC notifications are server-backed and are not deleted here.
   */
  const clearPastReports = useCallback(() => {
    reportNotificationStore.clear();
    setNotifications((prev) => prev.filter((n) => n._source !== 'report'));
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  // Poll for unread count
  useEffect(() => {
    if (!enablePolling || !userId || !isAuthenticated) return;

    fetchUnreadCount();
    checkReportCompletion();
    pollRef.current = setInterval(() => {
      fetchUnreadCount();
      checkReportCompletion();
    }, POLL_INTERVAL);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [enablePolling, userId, isAuthenticated, fetchUnreadCount, checkReportCompletion]);

  // Re-count immediately whenever a report notification is added/changed
  useEffect(() => {
    const handler = () => fetchUnreadCount();
    window.addEventListener(REPORT_NOTIFY_EVENT, handler);
    return () => window.removeEventListener(REPORT_NOTIFY_EVENT, handler);
  }, [fetchUnreadCount]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    userId,

    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    clearPastReports,
  };
};

export default useNotifications;
